/* Owner asked for the header trust row (free shipping / cash back /
   returns / Trustpilot) to auto-advance every ~4s below the 1320px
   breakpoint where it's a carousel (see llm-header.css) instead of
   sitting static, waiting for a manual swipe/tap. The stock
   carousel-slider component (slider.js) has no built-in autoplay, so
   this just drives its own existing prev/next buttons on a timer,
   reusing the same buttons a real click would use (rather than
   reaching into the component's internal scroll math directly) so it
   stays in sync with whatever state the component itself thinks it's
   in. Loops back to the first slide once nextBtn reports disabled
   (the component's own "last slide visible" signal - see
   setButtonStates() in slider.js) instead of doing nothing.

   Pauses on user interaction (hover on desktop, touch on mobile) so it
   doesn't fight someone actively swiping through it, and stops
   permanently after a manual prev/next click - standard carousel
   convention: autoplay is a convenience for people not engaging with
   it, not something that should yank control away from someone who
   is. */
(function () {
  var INTERVAL_MS = 4000;

  function setup(root) {
    var carousel = root.querySelector('carousel-slider');
    if (!carousel) return;

    var timer = null;
    var isAutoplayClick = false;

    /* Advance by exactly ONE badge per tick (owner 2026-09-04).
       This used to click the component's own next button, which scrolls a full PAGE. That was fine
       while every slide filled the viewport (the header row below 1320px), but the product trust row
       now shows ~3 badges plus a peek — so one page-click consumed nearly the whole scroll range,
       hit the end, and rewound on the following tick. The visible result was the row twitching left
       and right with the middle badges never moving off screen, rather than cycling through them.

       Measuring one item + the grid gap keeps the step meaningful at any width, and matches the
       header row too, where one item IS one page. */
    function tick() {
      var slider = carousel.querySelector('.slider');
      var grid = slider && slider.querySelector('.slider__grid');
      var firstItem = grid && grid.firstElementChild;
      if (!slider || !firstItem) return;

      var max = slider.scrollWidth - slider.clientWidth;
      if (max <= 2) return;              // everything fits — nothing to advance through

      var gap = parseFloat(window.getComputedStyle(grid).columnGap) || 0;
      var step = firstItem.getBoundingClientRect().width + gap;

      /* CONTINUOUS loop (owner 2026-09-04) — no rewind.

         Previously this ran to the end and scrolled smoothly back to 0, which is a long backwards
         sweep past every badge. Instead, after each step the badge that has just left the viewport
         is moved to the BACK of the row and the scroll position is reduced by exactly its width, so
         the visible content never moves but the order has rotated by one. The carousel therefore has
         no end state to rewind from and cycles forever in one direction.

         No cloning: duplicated badges would still be in the DOM at >=1200px where all four fit
         statically, and would overflow a row that is meant not to scroll at all. Rotating the real
         nodes keeps the element count fixed at every width.

         The compensation is exact because `step` is measured from the very item being moved, so the
         content shifts left by precisely the amount subtracted — no drift accumulates over hours. */
      /* Animated by hand rather than scrollTo({behavior:'smooth'}).
         The stock slider sets scroll-snap-type:x mandatory, and a programmatic smooth scroll against
         a mandatory snap container is dropped outright — verified live: scrollLeft stayed at 0 for
         the whole animation window. (llm-product-trust-v2.css now turns snapping off for this
         carousel; this tween is what actually moves it, and it works with or without snap.) */
      animateBy(slider, step, function () {
        if (!firstItem.isConnected || grid.firstElementChild !== firstItem) return;  // user intervened
        grid.appendChild(firstItem);
        slider.scrollLeft = Math.max(0, slider.scrollLeft - step);
      });
    }

    /* Ease the scroll position over ~450ms. rAF rather than CSS so the exact end value is ours:
       the rotation below subtracts `step` and any discrepancy would show as a visible jump. */
    function animateBy(el, distance, done) {
      var start = el.scrollLeft;
      var t0 = null;
      var DUR = 450;
      function frame(ts) {
        if (t0 === null) t0 = ts;
        var p = Math.min((ts - t0) / DUR, 1);
        var eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;   // easeInOutQuad
        el.scrollLeft = start + distance * eased;
        if (p < 1) { window.requestAnimationFrame(frame); return; }
        el.scrollLeft = start + distance;      // land exactly, no sub-pixel residue
        done();
      }
      window.requestAnimationFrame(frame);
    }

    function start() {
      if (timer) return;
      timer = window.setInterval(tick, INTERVAL_MS);
    }

    // Real bug caught before shipping: the component upgrades/measures
    // itself lazily (window.initLazyScript in slider.js, gated behind
    // an IntersectionObserver) and only removes [inactive] once that
    // finishes - a single delayed check for [inactive] racing that
    // async init sometimes ran just before it cleared, and with nothing
    // re-checking afterward, autoplay silently never started at all.
    // Polling until it's confirmed gone (or giving up after a generous
    // window) instead of trusting one fixed delay to land after it.
    //
    // Owner later confirmed on a real phone that the nav arrows never
    // appeared at all AND autoplay never ran - not just this file's own
    // symptom, but the underlying [inactive] flag itself never
    // clearing. That flag is what stock CSS gates the arrows' entire
    // visibility on (.carousel[inactive] > .slider-nav { display: none
    // }, higher specificity than any override short of !important), so
    // whatever prevents the component's own lazy IntersectionObserver
    // from firing breaks the arrows too, not just autoplay - polling
    // alone can't fix that, since it never forces initialization,
    // only waits for it. As a last resort, force the component's own
    // init() directly instead of continuing to wait indefinitely -
    // init() is a normal public method on the custom element instance,
    // safe to call once (guarded by the same [loaded] attribute it
    // itself sets, so this can't double-run if the observer does
    // eventually fire on its own).
    function waitForActive(attemptsLeft) {
      if (!carousel.hasAttribute('inactive')) {
        start();
        return;
      }
      if (attemptsLeft <= 0) {
        if (!carousel.hasAttribute('loaded') && typeof carousel.init === 'function') {
          carousel.init();
          start();
        }
        return;
      }
      window.setTimeout(function () { waitForActive(attemptsLeft - 1); }, 300);
    }

    function stop() {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    }

    function resumeAfterTouch() {
      window.setTimeout(start, INTERVAL_MS);
    }

    function stopPermanently() {
      stop();
      carousel.removeEventListener('mouseenter', stop);
      carousel.removeEventListener('mouseleave', start);
      carousel.removeEventListener('touchstart', stop);
      carousel.removeEventListener('touchend', resumeAfterTouch);
      carousel.removeEventListener('click', onManualNav);
    }

    function onManualNav(evt) {
      if (isAutoplayClick) return;
      if (evt.target.closest('.slider-nav__btn')) stopPermanently();
    }

    carousel.addEventListener('mouseenter', stop);
    carousel.addEventListener('mouseleave', start);
    // Owner reported the product-page trust carousel never auto-scrolled
    // on a real phone. Cause: touchstart -> stopPermanently killed
    // autoplay for good the instant a finger touched the carousel -
    // including a plain page-scroll whose touch simply happened to start
    // over it on the way past. Treat touch as a temporary pause instead
    // (resume a cycle after the finger lifts); a genuine arrow tap still
    // stops it permanently via onManualNav below, which is the real
    // "user took control" signal we actually want to honor.
    carousel.addEventListener('touchstart', stop, { passive: true });
    carousel.addEventListener('touchend', resumeAfterTouch, { passive: true });
    carousel.addEventListener('click', onManualNav);

    waitForActive(20);
  }

  // Also drives the product-page trust-badge carousel (.llm-trust-
   // carousel), which reuses the same stock carousel-slider + this
   // autoplay - see snippets/llm-product-trust-block.liquid.
  document.querySelectorAll('.llm-header-trust-row, .llm-trust-carousel').forEach(setup);
})();
